import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Container, Progress } from "reactstrap";
import axios from "axios";
import { FiCheckCircle, FiClock, FiCreditCard, FiMapPin, FiPackage, FiTruck } from "react-icons/fi";
import Helmet from "../components/Helmet/Helmet";
import { currencyFormatter, getApiUrl, getMediaUrl, siteConfig } from "../utils/site";

const trackingSteps = [
  { key: "pending", label: "Order placed", icon: <FiClock /> },
  { key: "success", label: "Payment confirmed", icon: <FiCreditCard /> },
  { key: "shipped", label: "In delivery", icon: <FiTruck /> },
  { key: "delivered", label: "Delivered", icon: <FiCheckCircle /> },
];

const deliveryMessages = {
  pending: "Your order has been created and is waiting for the next fulfilment step.",
  success: "Payment is confirmed and your jewellery is being prepared for dispatch.",
  shipped: "Your order is now moving through the delivery journey.",
  delivered: "Your order has been delivered. Thank you for choosing Roha Jewellery.",
  cancelled: "This order was cancelled. Please contact Roha if you need support.",
};

const normalizeTrackingState = (order) => {
  if (!order) return "pending";

  const paymentStatus = order.payment_status_from_payments || order.payment_status || "pending";
  const deliveryStatus = order.delivery_status || "pending";

  if (deliveryStatus === "delivered") return "delivered";
  if (deliveryStatus === "shipped") return "shipped";
  if (paymentStatus === "success") return "success";
  return "pending";
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const pollingRef = useRef(null);

  useEffect(() => {
    if (/^\d+$/.test(orderId)) {
      const token = localStorage.getItem("token");
      axios
        .get(getApiUrl(`/api/orders/${orderId}`), {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const hashId = res.data.order?.hash_id;
          if (hashId && hashId !== orderId) {
            navigate(`/orders/${hashId}`, { replace: true });
          }
        })
        .catch(() => {});
    }
  }, [orderId, navigate]);

  const fetchOrder = async (withLoading = true) => {
    if (withLoading) setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(getApiUrl(`/api/orders/${orderId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      const nextOrder = res.data.order;
      const nextItems = res.data.items || [];
      const productIds = nextItems.map((item) => item.product_id);
      const productMap = {};

      await Promise.all(
        productIds.map(async (id) => {
          try {
            const productRes = await axios.get(getApiUrl(`/api/products/${id}`));
            productMap[id] = productRes.data;
          } catch (_error) {}
        })
      );

      setOrder(nextOrder);
      setItems(nextItems);
      setProducts(productMap);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch order details.");
    } finally {
      if (withLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!order) return undefined;

    const paymentStatus = order.payment_status_from_payments || order.payment_status;
    const keepPolling = !["success", "failed", "cancelled"].includes(paymentStatus);

    if (!keepPolling) {
      clearInterval(pollingRef.current);
      return undefined;
    }

    pollingRef.current = setInterval(() => {
      fetchOrder(false);
    }, 5000);

    return () => clearInterval(pollingRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const paymentStatus = order?.payment_status_from_payments || order?.payment_status || "pending";
  const trackingState = normalizeTrackingState(order);
  const currentStep = trackingSteps.findIndex((step) => step.key === trackingState);

  if (loading) {
    return <div className="roha-empty-state roha-page">Loading your order tracker...</div>;
  }

  if (error) {
    return <Alert color="danger" className="my-5 text-center">{error}</Alert>;
  }

  if (!order) {
    return <Alert color="warning" className="my-5 text-center">Order not found.</Alert>;
  }

  return (
    <>
      <Helmet
        title={`Track Order ${order.hash_id || order.id} | Roha Jewellery`}
        description="Track your Roha Jewellery order, review payment confirmation, and follow the delivery journey."
        canonical={`${siteConfig.url}/orders/${order.hash_id || order.id}`}
      />

      <main className="roha-page">
        <section className="roha-page-hero roha-page-hero--compact">
          <Container>
            <div className="roha-order-tracker">
              <div className="roha-order-tracker__hero">
                <p className="roha-eyebrow">Track order</p>
                <h1>Your Roha order is easy to follow from payment to delivery.</h1>
                <p>
                  Order reference <strong>{order.hash_id || order.id}</strong>. Payment status is{" "}
                  <strong>{paymentStatus}</strong> and delivery status is{" "}
                  <strong>{order.delivery_status || "pending"}</strong>.
                </p>
                <div className="roha-order-tracker__badges">
                  <span><FiCreditCard /> {paymentStatus}</span>
                  <span><FiPackage /> {order.delivery_status || "pending"}</span>
                  <span><FiMapPin /> {order.delivery_city}, {order.delivery_country}</span>
                </div>
              </div>

              <div className="roha-order-tracker__timeline">
                <div className="roha-order-tracker__steps">
                  {trackingSteps.map((step, index) => (
                    <div key={step.key} className={`roha-order-tracker__step ${currentStep >= index ? "is-active" : ""}`}>
                      <div className="roha-order-tracker__step-icon">{step.icon}</div>
                      <strong>{step.label}</strong>
                    </div>
                  ))}
                </div>
                <Progress
                  value={currentStep <= 0 ? 8 : (currentStep / (trackingSteps.length - 1)) * 100}
                  color="success"
                  className="roha-order-tracker__progress"
                />
                <p className="roha-order-tracker__message">
                  {deliveryMessages[order.delivery_status] || deliveryMessages[trackingState]}
                </p>
              </div>
            </div>
          </Container>
        </section>

        <section className="roha-section">
          <Container>
            <div className="roha-order-review">
              <div className="roha-order-review__summary">
                <div className="roha-checkout-card__header">
                  <div>
                    <p className="roha-eyebrow">Delivery details</p>
                    <h2>Where your order is going</h2>
                  </div>
                </div>

                <div className="roha-contact-panel__links">
                  <div>
                    <FiMapPin />
                    <span>
                      {order.delivery_address_line1}
                      {order.delivery_address_line2 ? `, ${order.delivery_address_line2}` : ""}
                      <br />
                      {order.delivery_city}, {order.delivery_state}, {order.delivery_postal_code}
                      <br />
                      {order.delivery_country}
                    </span>
                  </div>
                </div>

                {order.delivery_map_url ? (
                  <div className="roha-hero__actions">
                    <a href={order.delivery_map_url} target="_blank" rel="noopener noreferrer" className="roha-button roha-button--ghost">
                      View on map
                    </a>
                  </div>
                ) : null}
              </div>

              <div className="roha-order-review__next">
                <div className="roha-checkout-summary">
                  <div className="roha-checkout-summary__header">
                    <p className="roha-eyebrow">Order value</p>
                    <h2>Commercial summary</h2>
                    <p>Placed {new Date(order.created_at).toLocaleString()}</p>
                  </div>
                  <div className="roha-checkout-summary__total">
                    <span>Total</span>
                    <strong>{currencyFormatter.format(Number(order.total || 0))}</strong>
                  </div>
                  <div className="roha-checkout-summary__notes">
                    <p><FiCreditCard /> Payment: {paymentStatus}</p>
                    <p><FiPackage /> Delivery: {order.delivery_status || "pending"}</p>
                    <p><FiClock /> Updated as your order progresses</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="roha-checkout-card" style={{ marginTop: "2rem" }}>
              <div className="roha-checkout-card__header">
                <div>
                  <p className="roha-eyebrow">Order items</p>
                  <h2>The jewellery in this order</h2>
                </div>
                <Button color="primary" className="rounded-pill px-4" onClick={() => navigate("/my-profile")}>
                  Back to my profile
                </Button>
              </div>

              <div className="roha-checkout-summary__list">
                {items.map((item, index) => {
                  const product = products[item.product_id] || {};
                  return (
                    <div key={`${item.product_id}-${index}`} className="roha-order-review__item">
                      <div className="roha-order-review__item-media">
                        {product.image_url ? (
                          <img src={getMediaUrl(product.image_url)} alt={product.name || "Product"} />
                        ) : (
                          <div className="roha-order-review__placeholder"><FiPackage /></div>
                        )}
                      </div>
                      <div>
                        <strong>{product.name || `Product ${item.product_id}`}</strong>
                        <p>Quantity {item.quantity}</p>
                      </div>
                      <span>{currencyFormatter.format(Number(item.price || 0) * Number(item.quantity || 0))}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
};

export default OrderDetails;
